import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { UserPlus, Mail, Copy, Check, Lock, Trash2, ArrowLeft } from 'lucide-react';
import { api } from '../hooks/api.js';
import { useAuthStore } from '../stores/authStore.js';
import type { WorkspaceRole, WorkspaceInviteListItem } from '@blogforge/shared';
import { formatDate } from '../lib/billing-format.js';

// ─────────────────────────────────────────────────────────────────
// Workspace members — manage who belongs to the ACTIVE workspace:
// invite by email (magic accept link), change roles, remove members
// and copy pending invites. Owner/admin only; server enforces the
// owner guardrails (sole owner, owner-only changes).
// ─────────────────────────────────────────────────────────────────

const ROLE_META: Record<WorkspaceRole, string> = {
  owner: 'bg-accent/10 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]',
  admin: 'bg-ink/5 text-ink-soft',
  member: 'bg-ink/5 text-muted',
};

export function MembersPage() {
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const qc = useQueryClient();

  const { data: workspacesData } = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => api.workspaces.list(),
  });
  const workspace = workspacesData?.workspaces.find((w) => w.id === activeWorkspaceId);
  const myRole = workspace?.role ?? 'member';
  const canManage = myRole === 'owner' || myRole === 'admin';

  const membersQuery = useQuery({
    queryKey: ['workspace', 'members', activeWorkspaceId],
    queryFn: () => api.workspaces.members(activeWorkspaceId!),
    enabled: Boolean(activeWorkspaceId) && canManage,
  });
  const invitesQuery = useQuery({
    queryKey: ['workspace', 'invites', activeWorkspaceId],
    queryFn: () => api.workspaces.invites(activeWorkspaceId!),
    enabled: Boolean(activeWorkspaceId) && canManage,
  });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['workspace', 'members', activeWorkspaceId] });
    void qc.invalidateQueries({ queryKey: ['workspace', 'invites', activeWorkspaceId] });
    void qc.invalidateQueries({ queryKey: ['workspaces'] });
  };

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const copyAcceptLink = async (invite: WorkspaceInviteListItem) => {
    await navigator.clipboard.writeText(invite.acceptUrl);
    setCopied(invite.id);
    setTimeout(() => setCopied(null), 1500);
  };

  if (!canManage) {
    return (
      <Centered>
        <Lock size={22} className="text-muted" />
        <h2 className="text-ink text-base font-semibold">Owners only</h2>
        <p className="text-muted max-w-sm text-[13px]">
          Only the workspace owner or an admin can manage members. Ask them to invite you or promote
          your role.
        </p>
        <Link to="/posts" className="btn-ghost mt-4 px-4 py-2 text-[13px]">
          <ArrowLeft size={14} />
          Back to posts
        </Link>
      </Centered>
    );
  }

  const members = membersQuery.data?.members ?? [];
  const invites = invitesQuery.data?.invites ?? [];
  const meId = useAuthStore.getState().user?.id;

  return (
    <div>
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 px-8 pt-8 pb-6">
        <div>
          <p className="kicker mb-2">
            <span className="bg-accent h-1.5 w-1.5 rounded-full" />
            Workspace settings
          </p>
          <h1 className="text-ink text-[22px] font-bold tracking-tight">Members</h1>
          <p className="text-ink-soft mt-1 text-[13px]">
            {workspace?.name ?? 'This workspace'} — invite teammates, change roles, remove members.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setInviteOpen(true)}
          className="btn-primary px-4 py-2 text-[13px]"
        >
          <UserPlus size={15} strokeWidth={2.4} />
          Invite member
        </button>
      </div>

      {error && (
        <p className="border-danger/20 bg-danger/8 text-danger mb-4 rounded-lg border px-3 py-2 text-[12.5px]">
          {error}
        </p>
      )}

      {/* ── Members list ────────────────────────────────────── */}
      <h2 className="text-ink mb-3 px-8 text-[15px] font-bold tracking-tight">
        People
        <span className="text-muted ml-2 text-[12px] font-medium">
          {members.length} member{members.length === 1 ? '' : 's'}
        </span>
      </h2>

      {membersQuery.isLoading ? (
        <div className="text-muted flex h-32 items-center justify-center gap-2 px-8 text-sm">
          <span className="border-line-strong border-t-accent h-3.5 w-3.5 animate-spin rounded-full border-2" />
          Loading members…
        </div>
      ) : (
        <div className="space-y-2.5 px-8">
          {members.map((member) => {
            const isSelf = member.userId === meId;
            return (
              <div key={member.userId} className="surface-row flex items-center gap-4 px-4 py-3">
                <div className="bg-primary text-primary-ink flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[12px] font-bold uppercase">
                  {(member.name ?? member.email).charAt(0)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-ink truncate text-sm font-semibold">
                    {member.name ?? '—'}
                    {isSelf && (
                      <span className="text-muted ml-2 text-[11px] font-medium">(you)</span>
                    )}
                  </p>
                  <p className="text-muted truncate text-[11.5px]">{member.email}</p>
                </div>

                <span
                  className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold capitalize ${ROLE_META[member.role]}`}
                >
                  {member.role}
                </span>

                {!isSelf && (
                  <select
                    aria-label={`Role of ${member.email}`}
                    value={member.role}
                    onChange={(e) =>
                      void run(() =>
                        api.workspaces.changeRole(
                          activeWorkspaceId!,
                          member.userId,
                          e.target.value as WorkspaceRole,
                        ),
                      )
                    }
                    className="field-input text-ink w-28 cursor-pointer px-2 py-1.5 text-[12px] font-medium"
                  >
                    {(['member', 'admin', 'owner'] as WorkspaceRole[]).map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                )}

                {!isSelf && (
                  <button
                    type="button"
                    title="Remove member"
                    onClick={() => {
                      if (confirm(`Remove ${member.email} from this workspace?`)) {
                        void run(() =>
                          api.workspaces.removeMember(activeWorkspaceId!, member.userId),
                        );
                      }
                    }}
                    className="btn-ghost text-danger hover:bg-danger/10 hover:text-danger px-2 text-xs"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Pending invites ─────────────────────────────────── */}
      <div className="mt-8 px-8">
        <h2 className="text-ink mb-3 text-[15px] font-bold tracking-tight">
          Pending invites
          <span className="text-muted ml-2 text-[12px] font-medium">
            {invites.length} outstanding
          </span>
        </h2>

        {invites.length === 0 ? (
          <div className="surface-row text-muted flex items-center gap-3 px-4 py-4 text-[13px]">
            <Mail size={14} />
            No outstanding invites. Use “Invite member” to share the workspace.
          </div>
        ) : (
          <div className="space-y-2.5">
            {invites.map((invite) => (
              <div
                key={invite.id}
                className="surface-row flex flex-wrap items-center gap-3 px-4 py-3"
              >
                <div className="bg-bg text-muted flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
                  <Mail size={13} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-ink truncate text-sm font-semibold">{invite.email}</p>
                  <p className="text-muted text-[11px]">
                    {invite.role} · expires{' '}
                    {invite.expiresAt ? formatDate(invite.expiresAt) : 'never'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void copyAcceptLink(invite)}
                  className="btn-ghost px-3 py-1.5 text-xs"
                >
                  {copied === invite.id ? <Check size={13} /> : <Copy size={13} />}
                  {copied === invite.id ? 'Copied' : 'Copy invite link'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Invite modal ────────────────────────────────────── */}
      {inviteOpen && (
        <InviteModal
          workspaceId={activeWorkspaceId!}
          canInviteOwner={myRole === 'owner'}
          onClose={() => setInviteOpen(false)}
          onInvited={() => {
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Invite modal
// ─────────────────────────────────────────────────────────────────
function InviteModal({
  workspaceId,
  canInviteOwner,
  onClose,
  onInvited,
}: {
  workspaceId: string;
  canInviteOwner: boolean;
  onClose: () => void;
  onInvited: () => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<WorkspaceRole>('member');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ email: string; acceptUrl: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    if (!email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.workspaces.invite(workspaceId, {
        email: email.trim(),
        role,
      });
      setCreated({ email: email.trim(), acceptUrl: result.acceptUrl });
      setCopied(false);
      onInvited();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!created) return;
    await navigator.clipboard.writeText(created.acceptUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="bg-ink/40 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="surface-row w-full max-w-sm p-5">
        {created ? (
          <div className="text-center">
            <div className="bg-accent/10 text-accent mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl">
              <Check size={20} />
            </div>
            <h3 className="text-ink text-[15px] font-bold">Invite sent to {created.email}</h3>
            <p className="text-muted mt-1 text-[12.5px]">
              Share the accept link — delivery by email isn’t wired up yet.
            </p>
            <button
              type="button"
              onClick={() => void copy()}
              className="btn-primary mt-4 w-full justify-center px-3 py-2 text-[13px]"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied to clipboard' : 'Copy accept link'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost mt-2 w-full justify-center px-3 py-2 text-[13px]"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-ink text-[15px] font-bold tracking-tight">Invite member</h3>
                <p className="text-muted mt-0.5 text-[12px]">
                  They’ll need an account with this email to accept.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="btn-ghost h-8 w-8 rounded-lg px-0"
              >
                ✕
              </button>
            </div>

            <label className="field-label mt-4 block">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@example.com"
              className="field-input w-full"
            />

            <label className="field-label mt-3 block">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as WorkspaceRole)}
              className="field-input w-full cursor-pointer"
            >
              {(['member', 'admin'] as WorkspaceRole[]).map((r) => (
                <option key={r} value={r}>
                  {r === 'admin' ? 'Admin — can manage members' : 'Member — can use the workspace'}
                </option>
              ))}
              {canInviteOwner && (
                <option value="owner">Owner — full billing + membership control</option>
              )}
            </select>

            {error && (
              <p className="border-danger/20 bg-danger/8 text-danger mt-3 rounded-lg border px-3 py-2 text-[12px]">
                {error}
              </p>
            )}

            <button
              type="button"
              onClick={() => void submit()}
              disabled={busy || !email.trim()}
              className="btn-primary mt-4 w-full justify-center px-3 py-2 text-[13px]"
            >
              <UserPlus size={14} />
              {busy ? 'Inviting…' : 'Send invite'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
      {children}
    </div>
  );
}
